import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import BigNumber from 'bignumber.js';
import { debounce } from 'lodash';
import { CONTRACTS, DEFAULT_CONTRACT, ContractSpec } from '../config/contracts.ts';
import RustCalculatorApi from '../../../api/rustApi';
import { computeMultiExits, defaultTpExits } from '../../../utils/multiExits';

BigNumber.config({
  EXPONENTIAL_AT: 1e+9,
  DECIMAL_PLACES: 8,
  ROUNDING_MODE: BigNumber.ROUND_DOWN,
});

// Default fee structure (round turn per contract)
const DEFAULT_FEES = {
  brokerCommission: 2.00,    // Per side × 2 = $4.00 round turn
  exchangeFee: 1.32,         // Per side × 2 = $2.64 round turn
  nfaFee: 0.02,              // Per side × 2 = $0.04 round turn
  clearingFee: 0.20,         // Per side × 2 = $0.40 round turn
};

// Calculate total round turn fee per contract
const calculateRoundTurnFee = (fees = DEFAULT_FEES) => {
  return (fees.brokerCommission + fees.exchangeFee + fees.nfaFee + fees.clearingFee) * 2;
};

export interface FuturesValues {
  contract: string;
  entryPrice: BigNumber;
  stopLoss: BigNumber;
  // NOTE: No takeProfit in values - it's CALCULATED from desiredRR (same as Crypto)
  risk: BigNumber;
  riskUnit: 'fixed' | 'percent';
  accountBalance: BigNumber;
  roundTurnFee: BigNumber;
  desiredRR: BigNumber;  // Desired Net R:R (trader wants this ratio after fees)
  riskOvershootTolerance: number;  // % tolerance for ceil rounding (0-50, default 0)
  // Multiple take-profits (partial exits)
  multiTpEnabled: boolean;
  tpCount: number;
  tpMode: 'percent' | 'qty';
  tpExits: { alloc: BigNumber; rMultiple: BigNumber }[];
}

export interface FuturesMetrics {
  contracts: number;
  positionType: 'LONG' | 'SHORT';
  stopPoints: BigNumber;
  targetPoints: BigNumber;           // Nominal target distance (from desiredRR)
  takeProfit: BigNumber;             // Nominal TP price (calculated from desiredRR)
  grossRisk: BigNumber;
  netRisk: BigNumber;
  grossProfit: BigNumber;
  netProfit: BigNumber;
  grossRR: BigNumber;
  netRR: BigNumber;
  totalFees: BigNumber;
  breakEvenPoints: BigNumber;
  marginRequired: BigNumber;
  marginUtilization: BigNumber;
  notionalValue: BigNumber;          // Position notional value
  requiredGrossRR: BigNumber;        // Gross R:R needed to achieve desired Net R:R
  optimizedTargetPoints: BigNumber;  // Points needed for required gross R:R
  optimizedTakeProfit: BigNumber;    // TP price for required gross R:R (for exact net R:R)
  // Position sizing discreteness (floor/ceil variants)
  floorContracts: number;            // Conservative: round down
  ceilContracts: number;             // Aggressive: round up
  floorRisk: BigNumber;              // Actual risk with floor contracts
  ceilRisk: BigNumber;               // Actual risk with ceil contracts
  targetRisk: BigNumber;             // Target risk budget
  floorDeviation: number;            // % deviation from target (negative)
  ceilDeviation: number;             // % deviation from target (positive)
  riskPerContract: BigNumber;        // $ risk per single contract
  selectedVariant: 'floor' | 'ceil'; // Which variant is selected based on tolerance
  multiExit?: any;                   // Multiple take-profits breakdown (when enabled)
}

const defaultValues: FuturesValues = {
  contract: DEFAULT_CONTRACT,
  entryPrice: new BigNumber(5100),
  stopLoss: new BigNumber(5090),
  // NOTE: No takeProfit - it's calculated from desiredRR
  risk: new BigNumber(500),
  riskUnit: 'fixed',
  accountBalance: new BigNumber(50000),
  roundTurnFee: new BigNumber(calculateRoundTurnFee()),
  desiredRR: new BigNumber(3),  // Default: trader wants 3:1 net R:R
  riskOvershootTolerance: 0,    // Default: no tolerance (always use floor/conservative)
  // Multiple take-profits (partial exits). Disabled → single TP as before.
  multiTpEnabled: false,
  tpCount: 3,
  tpMode: 'percent',
  tpExits: defaultTpExits(),
};

const defaultMetrics: FuturesMetrics = {
  contracts: 0,
  positionType: 'LONG',
  stopPoints: new BigNumber(0),
  targetPoints: new BigNumber(0),
  takeProfit: new BigNumber(0),
  grossRisk: new BigNumber(0),
  netRisk: new BigNumber(0),
  grossProfit: new BigNumber(0),
  netProfit: new BigNumber(0),
  grossRR: new BigNumber(0),
  netRR: new BigNumber(0),
  totalFees: new BigNumber(0),
  breakEvenPoints: new BigNumber(0),
  marginRequired: new BigNumber(0),
  marginUtilization: new BigNumber(0),
  notionalValue: new BigNumber(0),
  requiredGrossRR: new BigNumber(0),
  optimizedTargetPoints: new BigNumber(0),
  optimizedTakeProfit: new BigNumber(0),
  // Position sizing discreteness
  floorContracts: 0,
  ceilContracts: 0,
  floorRisk: new BigNumber(0),
  ceilRisk: new BigNumber(0),
  targetRisk: new BigNumber(0),
  floorDeviation: 0,
  ceilDeviation: 0,
  riskPerContract: new BigNumber(0),
  selectedVariant: 'floor',
  multiExit: null,
};

const LOCALSTORAGE_KEY = 'futuresCalculatorSettings';

// Serialize/deserialize for localStorage
const serializeValues = (values: FuturesValues): string => {
  const serialized: Record<string, any> = {};
  Object.entries(values).forEach(([key, value]) => {
    if (key === 'tpExits' && Array.isArray(value)) {
      serialized[key] = value.map((e: any) => ({
        alloc: e.alloc instanceof BigNumber ? e.alloc.toString() : e.alloc,
        rMultiple: e.rMultiple instanceof BigNumber ? e.rMultiple.toString() : e.rMultiple,
      }));
    } else if (value instanceof BigNumber) {
      serialized[key] = value.toString();
    } else {
      serialized[key] = value;
    }
  });
  return JSON.stringify(serialized);
};

const deserializeValues = (data: string): FuturesValues => {
  try {
    const parsed = JSON.parse(data);
    const result: FuturesValues = { ...defaultValues };

    Object.entries(parsed).forEach(([key, value]) => {
      if (key === 'contract' || key === 'riskUnit' || key === 'tpMode') {
        (result as any)[key] = value;
      } else if (key === 'multiTpEnabled') {
        (result as any)[key] = Boolean(value);
      } else if (key === 'riskOvershootTolerance') {
        // Handle number fields directly
        (result as any)[key] = typeof value === 'number' ? value : 0;
      } else if (key === 'tpCount') {
        (result as any)[key] = typeof value === 'number' ? value : 3;
      } else if (key === 'tpExits') {
        (result as any)[key] = Array.isArray(value)
          ? value.map((e: any) => ({ alloc: new BigNumber(e.alloc), rMultiple: new BigNumber(e.rMultiple) }))
          : defaultTpExits();
      } else if (typeof value === 'string' && key in defaultValues) {
        try {
          (result as any)[key] = new BigNumber(value);
        } catch (e) {
          // Keep default
        }
      }
    });

    return result;
  } catch (e) {
    return defaultValues;
  }
};

const loadFromLocalStorage = (): FuturesValues => {
  try {
    const stored = localStorage.getItem(LOCALSTORAGE_KEY);
    if (stored) {
      return deserializeValues(stored);
    }
  } catch (e) {
    console.warn('Failed to load futures settings from localStorage:', e);
  }
  return defaultValues;
};

const saveToLocalStorage = (values: FuturesValues) => {
  try {
    localStorage.setItem(LOCALSTORAGE_KEY, serializeValues(values));
  } catch (e) {
    console.warn('Failed to save futures settings to localStorage:', e);
  }
};

// Flag to control whether to use backend API or local calculations
// Set to true to use backend API (with local fallback)
const USE_BACKEND_API = true;
const CALCULATION_DELAY = 100;

export const useFuturesCalculator = () => {
  const [values, setValues] = useState<FuturesValues>(() => loadFromLocalStorage());
  const [metrics, setMetrics] = useState<FuturesMetrics>(defaultMetrics);
  const [isCalculating, setIsCalculating] = useState(false);
  const [useLocalFallback] = useState(false);
  const lastRequestId = useRef(0);

  // Track the discrete contract anchor for stable toggling between floor/ceil options
  // The anchor is only recomputed when risk is NOT at one of the known boundary values
  const discreteAnchorRef = useRef<{ floor: number; ceil: number } | null>(null);

  // Get current contract spec
  const contractSpec: ContractSpec = CONTRACTS[values.contract] || CONTRACTS[DEFAULT_CONTRACT];

  // Debounced save to localStorage
  const debouncedSave = useMemo(
    () => debounce((v: FuturesValues) => saveToLocalStorage(v), 500),
    []
  );

  // Save to localStorage when values change
  useEffect(() => {
    debouncedSave(values);
    return () => debouncedSave.cancel();
  }, [values, debouncedSave]);

  // Local calculation (used as fallback or when API is disabled)
  const calculateLocal = useCallback(() => {
    const { entryPrice, stopLoss, risk, riskUnit, accountBalance, roundTurnFee, desiredRR, riskOvershootTolerance } = values;
    const { pointValue, dayMargin } = contractSpec;

    // Determine position type
    const isLong = stopLoss.isLessThan(entryPrice);
    const positionType = isLong ? 'LONG' : 'SHORT';

    // Calculate stop distance in points
    const stopPoints = isLong
      ? entryPrice.minus(stopLoss).abs()
      : stopLoss.minus(entryPrice).abs();

    // Calculate target distance from desiredRR (NOT from user input)
    // This is the key difference: TP is CALCULATED, not INPUT
    const targetPoints = stopPoints.times(desiredRR);

    // Calculate nominal take profit price
    const takeProfit = isLong
      ? entryPrice.plus(targetPoints)
      : entryPrice.minus(targetPoints);

    // Calculate dollar risk amount (target risk budget)
    let targetRisk: BigNumber;
    if (riskUnit === 'percent') {
      targetRisk = accountBalance.times(risk).dividedBy(100);
    } else {
      targetRisk = risk;
    }

    // Calculate risk per contract (stop points × point value + round turn fee)
    const riskPerContractGross = stopPoints.times(pointValue);
    const riskPerContract = riskPerContractGross.plus(roundTurnFee);

    // Prevent division by zero
    if (riskPerContract.isZero() || riskPerContract.isNaN()) {
      return defaultMetrics;
    }

    // === POSITION SIZING DISCRETENESS WITH STABLE ANCHORING ===
    // The anchor ensures stable options when toggling between Conservative/Aggressive
    // It only recomputes when risk is NOT at one of the known boundary values

    let floorContracts = 0;
    let ceilContracts = 0;
    let floorRisk = new BigNumber(0);
    let ceilRisk = new BigNumber(0);
    let floorDeviation = 0;
    let ceilDeviation = 0;
    let selectedVariant: 'floor' | 'ceil' = 'floor';

    if (riskPerContract.gt(0)) {
      const existingAnchor = discreteAnchorRef.current;

      // Calculate what floor and ceil risk WOULD be for the current anchor
      const anchorFloorRisk = existingAnchor ? riskPerContract.times(existingAnchor.floor) : null;
      const anchorCeilRisk = existingAnchor ? riskPerContract.times(existingAnchor.ceil) : null;

      // Check if current targetRisk matches one of the anchor boundaries
      const tolerance = new BigNumber(0.01);
      const isAtAnchorFloor = anchorFloorRisk && targetRisk.minus(anchorFloorRisk).abs().isLessThan(tolerance);
      const isAtAnchorCeil = anchorCeilRisk && targetRisk.minus(anchorCeilRisk).abs().isLessThan(tolerance);

      // If no anchor, or targetRisk is NOT at either anchor boundary, recompute anchor
      if (!existingAnchor || (!isAtAnchorFloor && !isAtAnchorCeil)) {
        const exactContracts = targetRisk.dividedBy(riskPerContract);
        const computedFloor = Math.max(0, exactContracts.integerValue(BigNumber.ROUND_DOWN).toNumber());
        const computedCeil = computedFloor + 1;
        discreteAnchorRef.current = { floor: computedFloor, ceil: computedCeil };
      }

      // Use the stable anchor for floor/ceil
      floorContracts = discreteAnchorRef.current!.floor;
      ceilContracts = discreteAnchorRef.current!.ceil;

      floorRisk = riskPerContract.times(floorContracts);
      ceilRisk = riskPerContract.times(ceilContracts);

      floorDeviation = targetRisk.isZero()
        ? 0
        : floorRisk.minus(targetRisk).dividedBy(targetRisk).times(100).toNumber();
      ceilDeviation = targetRisk.isZero()
        ? 0
        : ceilRisk.minus(targetRisk).dividedBy(targetRisk).times(100).toNumber();

      // Select variant based on tolerance (automatic selection only)
      // selectedVariant is for visual indication only, calculations always use current Risk
      selectedVariant = ceilDeviation <= riskOvershootTolerance ? 'ceil' : 'floor';
    }

    // Calculate actual contracts from current Risk (single source of truth)
    // The Risk in header determines the exact position size
    const exactContracts = riskPerContract.gt(0) ? targetRisk.dividedBy(riskPerContract) : new BigNumber(0);
    const contracts = exactContracts.integerValue(BigNumber.ROUND_HALF_UP).toNumber();

    // Ensure at least 0 contracts
    const safeContracts = Math.max(0, contracts);

    // Calculate gross risk/profit (without fees)
    const grossRisk = riskPerContractGross.times(safeContracts);
    const grossProfit = targetPoints.times(pointValue).times(safeContracts);

    // Calculate total fees for the trade
    const totalFees = roundTurnFee.times(safeContracts);

    // Calculate NET risk/profit (with fees)
    const netRisk = grossRisk.plus(totalFees);
    const netProfit = grossProfit.minus(totalFees);

    // Calculate R:R ratios
    const grossRR = grossRisk.isZero() ? new BigNumber(0) : grossProfit.dividedBy(grossRisk);
    const netRR = netRisk.isZero() ? new BigNumber(0) : netProfit.dividedBy(netRisk);

    // Calculate break-even distance (points needed to cover fees)
    const breakEvenPoints = safeContracts > 0
      ? totalFees.dividedBy(new BigNumber(pointValue).times(safeContracts))
      : new BigNumber(0);

    // Calculate margin required and utilization
    const marginRequired = new BigNumber(dayMargin).times(safeContracts);
    const marginUtilization = accountBalance.isZero()
      ? new BigNumber(0)
      : marginRequired.dividedBy(accountBalance).times(100);

    // Calculate Notional Value = Entry Price × Point Value × Contracts
    const notionalValue = entryPrice.times(pointValue).times(safeContracts);

    // Calculate Required Gross R:R to achieve desired Net R:R
    // Formula: requiredGrossRR = desiredNetRR + fees × (desiredNetRR + 1) / grossRisk
    const requiredGrossRR = grossRisk.isZero()
      ? new BigNumber(0)
      : desiredRR.plus(
          totalFees.times(desiredRR.plus(1)).dividedBy(grossRisk)
        );

    // Calculate Optimized TP distance (points needed for required gross R:R)
    const optimizedTargetPoints = stopPoints.times(requiredGrossRR);

    // Calculate Optimized TP price
    const optimizedTakeProfit = isLong
      ? entryPrice.plus(optimizedTargetPoints)
      : entryPrice.minus(optimizedTargetPoints);

    return {
      contracts: safeContracts,
      positionType: positionType as 'LONG' | 'SHORT',
      stopPoints,
      targetPoints,
      takeProfit,
      grossRisk,
      netRisk,
      grossProfit,
      netProfit,
      grossRR,
      netRR,
      totalFees,
      breakEvenPoints,
      marginRequired,
      marginUtilization,
      notionalValue,
      requiredGrossRR,
      optimizedTargetPoints,
      optimizedTakeProfit,
      // Position sizing discreteness (with stable anchoring)
      floorContracts,
      ceilContracts,
      floorRisk,
      ceilRisk,
      targetRisk,
      floorDeviation,
      ceilDeviation,
      riskPerContract,
      selectedVariant,
    };
  }, [values, contractSpec]);

  // Calculate metrics (API with local fallback)
  const calculateMetrics = useCallback(async () => {
    const currentRequestId = lastRequestId.current + 1;
    lastRequestId.current = currentRequestId;

    setIsCalculating(true);

    // Attach the multiple-take-profits breakdown (when enabled) to any metrics object.
    const withMultiExit = (m: FuturesMetrics): FuturesMetrics => {
      if (!values.multiTpEnabled) return { ...m, multiExit: null };
      const isLong = values.entryPrice.gt(values.stopLoss);
      const beP = m.breakEvenPoints || new BigNumber(0);
      const breakEvenPrice = isLong ? values.entryPrice.plus(beP) : values.entryPrice.minus(beP);
      return {
        ...m,
        // priceDecimals intentionally omitted — computeMultiExits' own default
        // (8dp) preserves full precision instead of capping to entry/stop's own
        // decimal count, which could be less than the fee-driven optimized TP needs.
        multiExit: computeMultiExits({
          entryPrice: values.entryPrice,
          stopDistance: values.entryPrice.minus(values.stopLoss).abs(),
          tpPathFee: m.totalFees,
          exits: values.tpExits,
          mode: values.tpMode,
          quantity: new BigNumber(m.contracts || 0),
          riskAmount: m.netRisk,
          breakEven: breakEvenPrice,
          optimizedTakeProfit: m.optimizedTakeProfit,
          nominalRR: values.desiredRR,
          side: isLong ? 'long' : 'short',
        }),
      };
    };

    try {
      // Try backend API if enabled and not in fallback mode
      if (USE_BACKEND_API && !useLocalFallback) {
        try {
          const requestBody = {
            contract: values.contract,
            entryPrice: values.entryPrice.toString(),
            stopLoss: values.stopLoss.toString(),
            // NOTE: No takeProfit - backend calculates it from desiredRR
            risk: values.risk.toString(),
            riskUnit: values.riskUnit,
            accountBalance: values.accountBalance.toString(),
            roundTurnFee: values.roundTurnFee.toString(),
            desiredRR: values.desiredRR.toString(),
            riskOvershootTolerance: values.riskOvershootTolerance,
          };

          console.log('[FuturesCalculator] Calling backend API...');
          const data = await RustCalculatorApi.calculateFutures(requestBody);

          // Check if this is still the latest request
          if (currentRequestId !== lastRequestId.current) return;

          console.log('[FuturesCalculator] API response:', data);

          // === Compute discreteness fields locally (backend doesn't support them yet) ===
          const stopPoints = new BigNumber(data.stopPoints);
          const { pointValue } = contractSpec;
          const { risk, riskUnit, accountBalance, roundTurnFee, riskOvershootTolerance } = values;

          // Calculate target risk budget
          let targetRisk: BigNumber;
          if (riskUnit === 'percent') {
            targetRisk = accountBalance.times(risk).dividedBy(100);
          } else {
            targetRisk = risk;
          }

          // Calculate risk per contract
          const riskPerContractGross = stopPoints.times(pointValue);
          const riskPerContract = riskPerContractGross.plus(roundTurnFee);

          // Calculate floor/ceil contracts
          let floorContracts = 0;
          let ceilContracts = 0;
          let floorRisk = new BigNumber(0);
          let ceilRisk = new BigNumber(0);
          let floorDeviation = 0;
          let ceilDeviation = 0;
          let selectedVariant: 'floor' | 'ceil' = 'floor';

          if (riskPerContract.gt(0)) {
            // === POSITION SIZING DISCRETENESS WITH STABLE ANCHORING ===
            // The anchor ensures stable options when toggling between Conservative/Aggressive
            // It only recomputes when risk is NOT at one of the known boundary values

            const existingAnchor = discreteAnchorRef.current;

            // Calculate what floor and ceil risk WOULD be for the current anchor
            const anchorFloorRisk = existingAnchor ? riskPerContract.times(existingAnchor.floor) : null;
            const anchorCeilRisk = existingAnchor ? riskPerContract.times(existingAnchor.ceil) : null;

            // Check if current targetRisk matches one of the anchor boundaries
            // Use small tolerance for floating point comparison
            const tolerance = new BigNumber(0.01);
            const isAtAnchorFloor = anchorFloorRisk && targetRisk.minus(anchorFloorRisk).abs().isLessThan(tolerance);
            const isAtAnchorCeil = anchorCeilRisk && targetRisk.minus(anchorCeilRisk).abs().isLessThan(tolerance);

            // If no anchor, or targetRisk is NOT at either anchor boundary, recompute anchor
            if (!existingAnchor || (!isAtAnchorFloor && !isAtAnchorCeil)) {
              const exactContracts = targetRisk.dividedBy(riskPerContract);
              const computedFloor = Math.max(0, exactContracts.integerValue(BigNumber.ROUND_DOWN).toNumber());
              // Ceil is always floor + 1 to ensure two distinct options for toggling
              const computedCeil = computedFloor + 1;
              discreteAnchorRef.current = { floor: computedFloor, ceil: computedCeil };
            }

            // Use the stable anchor for floor/ceil
            floorContracts = discreteAnchorRef.current!.floor;
            ceilContracts = discreteAnchorRef.current!.ceil;

            floorRisk = riskPerContract.times(floorContracts);
            ceilRisk = riskPerContract.times(ceilContracts);

            if (!targetRisk.isZero()) {
              floorDeviation = floorRisk.minus(targetRisk).dividedBy(targetRisk).times(100).toNumber();
              ceilDeviation = ceilRisk.minus(targetRisk).dividedBy(targetRisk).times(100).toNumber();
            }

            // Select variant based on tolerance (automatic selection for visual indication)
            // selectedVariant is for highlighting the recommended option, NOT for calculations
            // Calculations always use current Risk from header (single source of truth)
            selectedVariant = ceilDeviation <= riskOvershootTolerance ? 'ceil' : 'floor';
          }

          // Use API data directly - Risk in header is the single source of truth
          // Clicking Conservative/Aggressive cards will SET Risk to floorRisk/ceilRisk
          setMetrics(withMultiExit({
            contracts: data.contracts,
            positionType: data.positionType as 'LONG' | 'SHORT',
            stopPoints,
            targetPoints: new BigNumber(data.targetPoints),
            takeProfit: new BigNumber(data.takeProfit),
            grossRisk: new BigNumber(data.grossRisk),
            netRisk: new BigNumber(data.netRisk),
            grossProfit: new BigNumber(data.grossProfit),
            netProfit: new BigNumber(data.netProfit),
            grossRR: new BigNumber(data.grossRR),
            netRR: new BigNumber(data.netRR),
            totalFees: new BigNumber(data.totalFees),
            breakEvenPoints: new BigNumber(data.breakEvenPoints),
            marginRequired: new BigNumber(data.marginRequired),
            marginUtilization: new BigNumber(data.marginUtilization),
            notionalValue: new BigNumber(data.notionalValue),
            requiredGrossRR: new BigNumber(data.requiredGrossRR),
            optimizedTargetPoints: new BigNumber(data.optimizedTargetPoints),
            optimizedTakeProfit: new BigNumber(data.optimizedTakeProfit),
            // Position sizing discreteness (computed locally for display)
            floorContracts,
            ceilContracts,
            floorRisk,
            ceilRisk,
            targetRisk,
            floorDeviation,
            ceilDeviation,
            riskPerContract,
            selectedVariant,
          }));
          return;
        } catch (apiError) {
          console.warn('[FuturesCalculator] API error, falling back to local calculation:', apiError);
          // Don't set fallback permanently - try API on next calculation
          // setUseLocalFallback(true);
        }
      }

      // Local calculation (fallback or when API is disabled)
      if (currentRequestId !== lastRequestId.current) return;

      const localMetrics = calculateLocal();
      setMetrics(withMultiExit(localMetrics));
    } catch (error) {
      console.error('Futures calculation error:', error);
      setMetrics(defaultMetrics);
    } finally {
      if (currentRequestId === lastRequestId.current) {
        setIsCalculating(false);
      }
    }
  }, [values, calculateLocal, useLocalFallback, contractSpec]);

  // Debounced calculate metrics
  const debouncedCalculateMetrics = useMemo(
    () => debounce(calculateMetrics, CALCULATION_DELAY),
    [calculateMetrics]
  );

  // Recalculate when values change
  useEffect(() => {
    debouncedCalculateMetrics();
    return () => debouncedCalculateMetrics.cancel();
  }, [debouncedCalculateMetrics]);

  // Update a single value
  const updateValue = useCallback(<K extends keyof FuturesValues>(
    key: K,
    value: FuturesValues[K]
  ) => {
    setValues(prev => ({ ...prev, [key]: value }));
  }, []);

  // Update multiple values at once
  const updateValues = useCallback((newValues: Partial<FuturesValues>) => {
    setValues(prev => ({ ...prev, ...newValues }));
  }, []);

  // Change contract (and optionally reset prices)
  const changeContract = useCallback((symbol: string) => {
    const newSpec = CONTRACTS[symbol];
    if (newSpec) {
      setValues(prev => ({
        ...prev,
        contract: symbol,
      }));
    }
  }, []);

  return {
    values,
    setValues: updateValues,
    updateValue,
    metrics,
    contractSpec,
    isCalculating,
    changeContract,
    calculateMetrics,
  };
};

export default useFuturesCalculator;
